import { readJson, remove, writeJson } from '@/lib/storage';

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  email: string | null;
  uuid: string | null;
  /** Drives the `is_new_account` analytics user property. null = the server did not say. */
  isNewAccount?: boolean | null;
}

const KEY = 'facturita.auth';
type Listener = (t: Tokens | null) => void;
const listeners = new Set<Listener>();
let cache: Tokens | null | undefined;

/** Bearer tokens in localStorage (SPA). Notifies subscribers on change so the auth context updates. */
export const tokenStore = {
  get(): Tokens | null {
    if (cache === undefined) cache = readJson<Tokens | null>(KEY, null);
    return cache;
  },
  set(t: Tokens) {
    cache = t;
    writeJson(KEY, t);
    listeners.forEach((l) => l(t));
  },
  clear() {
    if (cache === null) return;
    cache = null;
    remove(KEY);
    listeners.forEach((l) => l(null));
  },
  subscribe(l: Listener) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
};

/** Parse the token pair every login endpoint returns. */
/** Tolerant read of whatever the server calls it; null when it says nothing. */
function newAccountFlag(json: Record<string, unknown>): boolean | null {
  for (const k of ['is_new_account', 'isNewAccount', 'is_new_user', 'isNewUser', 'created']) {
    const v = json[k];
    if (typeof v === 'boolean') return v;
    if (v === 'true' || v === 'false') return v === 'true';
  }
  return null;
}

export function tokensFromResponse(json: Record<string, unknown>, fallbackEmail?: string | null): Tokens {
  const access = String(json.access_token ?? json.accessToken ?? '');
  const refresh = String(json.refresh_token ?? json.refreshToken ?? '');
  if (!access) throw new Error('Respuesta de autenticación inválida');
  return {
    accessToken: access,
    refreshToken: refresh,
    email: (json.email as string | undefined) ?? fallbackEmail ?? null,
    uuid: (json.uuid as string | undefined) ?? null,
    isNewAccount: newAccountFlag(json),
  };
}
